/**
 * Parses a weight_per_variety field that may be a JSON array, a string, or absent.
 * Returns a plain array ready for groupByVarietyType().
 */
export const parseWeightVarieties = (raw) => {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw;
  if (typeof raw === 'string') {
    try { return JSON.parse(raw); } catch { return []; }
  }
  return [];
};

/**
 * Calculates box consolidation info from a variety/manifest item.
 * Returns { actualBoxes, presentationKg, equivBoxes82 } or nulls for missing data.
 */
export const calculateBoxes = (item) => {
  const netWeight = Number(item.weight_kg ?? item.net_weight) || 0;
  const commercialUnits = Number(item.commercial_units) || 0;
  const presentationRaw =
    item.presentation ?? item.weight_per_box ?? item.kg_per_box ?? item.box_weight ?? item.net_weight_per_box;
  const parsedPresentation =
    presentationRaw != null && presentationRaw !== '' ? parseFloat(presentationRaw) : NaN;

  let presentationKg = null;
  let actualBoxes = null;

  if (!isNaN(parsedPresentation) && parsedPresentation > 0) {
    presentationKg = parsedPresentation;
    actualBoxes = netWeight > 0 ? Math.round(netWeight / presentationKg) : null;
  } else if (netWeight > 0 && commercialUnits > 0) {
    presentationKg = Math.round((netWeight / commercialUnits) * 10) / 10;
    actualBoxes = commercialUnits;
  }

  const equivBoxes82 = netWeight > 0 ? Math.round(netWeight / 8.2) : null;

  return { actualBoxes, presentationKg, equivBoxes82 };
};

/**
 * Normalizes weight_per_variety data into a consistent grouped format.
 * Returns an array of { type, varieties: [{ variety, weight_kg }], subtotal }.
 *
 * Supports two input shapes:
 * 1. Pre-grouped (from updated BQ view):
 *    [{ type_of_variety, total_weight, varieties: [{ variety, weight_kg }] }]
 * 2. Flat (legacy or fallback):
 *    [{ variety, weight_kg, type_of_variety? }]
 */
export const groupByVarietyType = (weightVarieties) => {
  if (!weightVarieties || weightVarieties.length === 0) return [];

  let result;

  // Detect pre-grouped format: items have a `varieties` array
  if (Array.isArray(weightVarieties[0]?.varieties)) {
    result = weightVarieties.map((group) => ({
      type: group.type_of_variety || 'Total',
      varieties: group.varieties || [],
      subtotal: Number(group.total_weight) || Number(group.weight_kg) || group.varieties.reduce(
        (sum, v) => sum + (Number(v.weight_kg) || 0), 0
      ),
    }));
  } else {
    // Flat format: group by type_of_variety
    const groups = {};
    weightVarieties.forEach((item) => {
      const type = item.type_of_variety || 'Total';
      if (!groups[type]) {
        groups[type] = { type, varieties: [], subtotal: 0 };
      }
      groups[type].varieties.push(item);
      groups[type].subtotal += Number(item.weight_kg) || 0;
    });
    result = Object.values(groups);
  }

  // Sort groups: A→Z by type, "Total" last
  result.sort((a, b) => {
    if (a.type === 'Total') return 1;
    if (b.type === 'Total') return -1;
    return a.type.localeCompare(b.type);
  });

  // Sort varieties within each group A→Z by variety name
  result.forEach((group) => {
    group.varieties.sort((a, b) => (a.variety || '').localeCompare(b.variety || ''));
  });

  return result;
};

/**
 * Collapses rows sharing a variety name into a single entry, keeping the
 * original rows as `sources` so the UI can render them as subcounts.
 *
 * The same variety commonly arrives on several shipments for one ETA, which
 * would otherwise render as repeated sibling rows with no indication that they
 * roll up to one variety.
 *
 * Returns [{ variety, weight_kg, sources }] preserving input order.
 */
export const mergeVarietyRows = (varieties) => {
  const byName = new Map();

  (varieties || []).forEach((row) => {
    const variety = row.variety || 'Unknown';
    const key = variety.trim().toLowerCase();
    if (!byName.has(key)) {
      byName.set(key, { variety, weight_kg: 0, sources: [] });
    }
    const entry = byName.get(key);
    entry.weight_kg += Number(row.weight_kg) || 0;
    entry.sources.push(row);
  });

  return Array.from(byName.values());
};

export const DEFAULT_BOX_KG = 8.2;

export const convertWeight = (kg, unit = 'boxes', boxKg = DEFAULT_BOX_KG) => {
  if (!kg || kg <= 0) return 0;
  switch (unit) {
    case 'boxes': return Math.round(kg / boxKg);
    case 'lbs': return Math.round(kg * 2.20462);
    default: return Math.round(kg);
  }
};

export const weightUnitLabel = (unit) => {
  switch (unit) {
    case 'boxes': return 'boxes';
    case 'lbs': return 'lbs';
    default: return 'kg';
  }
};

export const weightUnitOptionLabel = (unit, boxKg = DEFAULT_BOX_KG) =>
  (unit === 'boxes' ? `boxes ${boxKg}kg` : weightUnitLabel(unit));

/**
 * Prefer weight_per_variety (granular rows from Bloom Lake), then weight_per_type.
 * Normalizes to: [{ commodity, total_weight, types: [...] }] for groupByVarietyType().
 */
export const getShipmentWeightNested = (shipment) => {
  const primary = parseWeightVarieties(shipment?.weight_per_variety);
  const fallback = parseWeightVarieties(shipment?.weight_per_type);
  const raw = primary.length > 0 ? primary : fallback;
  if (!raw.length) return [];

  // Nested weight_per_type shape: one group per commodity×type bucket from SQL.
  // After vessel-card concat, several groups can share a commodity — collapse so
  // ArrivalsList (.find) and ShipmentCard match getShipmentCommodityWeight sums.
  if (raw[0]?.types && Array.isArray(raw[0].types)) {
    const byCommodity = new Map();
    for (const c of raw) {
      const commodity = c.commodity ?? c.commodity_name ?? 'Unknown';
      const key = String(commodity).trim().toLowerCase();
      if (!byCommodity.has(key)) {
        byCommodity.set(key, { commodity, total_weight: 0, types: [] });
      }
      const entry = byCommodity.get(key);
      entry.total_weight += Number(c.total_weight) || 0;
      if (Array.isArray(c.types) && c.types.length) {
        entry.types.push(...c.types);
      }
    }
    return Array.from(byCommodity.values());
  }

  const byCommodity = {};
  for (const item of raw) {
    const comm = item.commodity ?? item.commodity_name;
    if (comm) {
      if (!byCommodity[comm]) byCommodity[comm] = [];
      byCommodity[comm].push(item);
    }
  }
  if (Object.keys(byCommodity).length > 0) {
    return Object.entries(byCommodity).map(([commodity, items]) => ({
      commodity,
      total_weight: items.reduce((s, i) => s + (Number(i.weight_kg) || 0), 0),
      types: items,
    }));
  }

  const total_weight = raw.reduce((s, i) => s + (Number(i.weight_kg) || 0), 0);
  return [{ commodity: null, total_weight, types: raw }];
};

/**
 * Weight (kg) of ONE commodity aboard a shipment, or null when it can't be known.
 *
 * `shipment.total_weight` covers every commodity in the hull, so summing it over a
 * commodity-filtered list over-reports: a vessel carrying oranges AND blueberries
 * contributes both. This narrows to `commodity` using the per-variety breakdown
 * (the same source ShipmentCard and MapSidebar already render from).
 *
 * With no `commodity` argument this is just the shipment total (unfiltered view).
 */
export const getShipmentCommodityWeight = (shipment, commodity) => {
  const total = Number(shipment?.total_weight) || 0;
  if (!commodity) return total;

  const target = String(commodity).trim().toLowerCase();

  const matched = getShipmentWeightNested(shipment).filter(
    (c) => c.commodity && String(c.commodity).trim().toLowerCase() === target,
  );
  if (matched.length > 0) {
    return matched.reduce((sum, c) => sum + (Number(c.total_weight) || 0), 0);
  }

  const carried = String(shipment?.commodities || '')
    .split(',')
    .map((c) => c.trim())
    .filter(Boolean);
  if (carried.length === 1 && carried[0].toLowerCase() === target) return total;

  return null;
};

/** Weighted average kg per box from variety rows (fallback 8.2). */
export const getSectionBoxKg = (groups) => {
  let kgSum = 0;
  let boxSum = 0;
  for (const g of groups || []) {
    for (const v of g.varieties || []) {
      const b = calculateBoxes(v);
      if (b.actualBoxes && b.presentationKg) {
        boxSum += b.actualBoxes;
        kgSum += Number(v.weight_kg) || 0;
      }
    }
  }
  if (boxSum > 0 && kgSum > 0) return Math.round((kgSum / boxSum) * 10) / 10;
  return 8.2;
};
